import { Injectable } from '@angular/core';
import { Status } from '@df/core/status';
import { App } from '@df/core/app';
import { Basket } from '@df/session/api/basket/basket';
import { Customer } from '@df/session/api/customer';
import flatten from 'lodash-es/flatten';
import isEqual from 'lodash-es/isEqual';
import sortBy from 'lodash-es/sortBy';
import { NGhostProducts } from '@df/module-ghost-products/ghost-products.namespace';

@Injectable({
    providedIn: 'root'
})
export class GhostProductsService {
    private loadPromise?: Promise<NGhostProducts.IOfferPrepared[]>;
    private offers?: NGhostProducts.IOfferPrepared[];
    private lastBasketItemIds?: number[];
    private lastBasketQty?: number;

    constructor(
        private app: App,
        private basket: Basket,
        private customer: Customer
    ) {
        basket.subscribe(() => {
            if (this.basketChanged) {
                this.loadPromise = undefined;
                this.offers = undefined;
            }
        });
    }

    private get basketChanged(): boolean {
        const itemsChanged = !isEqual(sortBy(this.basket.data?.items.map(el => el.id)), sortBy(this.lastBasketItemIds));
        const qtyChanged = this.basket.data.qty !== this.lastBasketQty;
        return itemsChanged || qtyChanged;
    }

    load(): Promise<NGhostProducts.IOfferPrepared[]> {
        if (this.offers) {
            return new Promise(r => r(this.offers!));
        }
        if (!this.loadPromise) {
            this.loadPromise = this.doLoad();
        }
        return this.loadPromise;
    }

    private async doLoad(): Promise<NGhostProducts.IOfferPrepared[]> {
        await this.customer.model.load();

        const quoteId = this.app.customer.basket.data.quote_id;

        if (!quoteId) {
            this.offers = [];
            return this.offers;
        }

        this.lastBasketItemIds = this.basket.data?.items.map(el => el.id);
        this.lastBasketQty = this.basket.data.qty;

        const data: NGhostProducts.ILoadRequest = { quote_id: quoteId };
        const qhostProductData = await this.app.request.send(undefined, { path: NGhostProducts.Endpoints.load, data });

        const ids = flatten(qhostProductData?.ghostProductsRules?.map(i => i.product_ids) || []) as number[];
        if (!ids.length) {
            this.offers = [];
            return this.offers;
        }

        await this.app.resources.product.loadAllIds(ids, 2, { safe: true });

        this.offers = qhostProductData.ghostProductsRules
            .reverse()
            .map(offer => {
                return {
                    name: offer.name,
                    subtitle: offer.subtitle,
                    ruleType: offer.rule_type,
                    products: this.app.resources.product
                        .getAllExisting(offer.product_ids)
                        .filter(model => model.isSalable(1) || model.isConfigurable)
                        .map(model => {
                            const status = new Status();
                            return {
                                model,
                                status,
                                getPrice: () => Math.floor(model.getPrice() * (100 - offer.dummy_discount)) * 0.01,
                                getOrgPrice: () => model.getOrgPrice() || model.getPrice(),
                                submit: () => this.app.customer.basket.add(status, model.id)
                            };
                        })
                };
            })
            .filter(i => i.products.length) as NGhostProducts.IOfferPrepared[];

        return this.offers;
    }
}
